import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { ClientAccess } from '../../components/portal/ClientAccess';
import { ClientLink } from '../../components/portal/ClientLink';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';
export function ProjectAdmin() {
  const {
    activeProject,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    startPreview,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    notify
  } = usePortal();
  const [editing, setEditing] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const reviewCount = project.deliverables.filter(item => item.stage === 'Review').length;
  const finalCount = project.deliverables.length - reviewCount;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setSavingTemplate(false);
    notify(`Template saved · ${name}`);
  }
  return <div data-id="element-736" data-mp="840g7przljbv">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-737" data-mp="22m1thiy4rmd4">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-738" data-mp="1sodyqt0iyeuy" />
        Client Projects
      </button>

      <header className="mt-10 border-b border-ruleDark pb-10" data-id="element-739" data-mp="2lc03i4lhnpje">
        <p className="label text-paper/40" data-id="element-740" data-mp="2laoyk1gb4bqp">Portal Project Admin</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-741" data-mp="2qb496l97f54z">
          {project.client.toUpperCase()}
        </h1>
        <p className="mt-6 font-display text-xl text-paper/70" data-id="element-742" data-mp="2gdgge0v5s7pg">{project.name}</p>
        <p className="label mt-5 text-paper/40" data-id="element-743" data-mp="2gf9mnf1v0pwx">
          {project.type} · {projectStatus(project)} · Updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-744" data-mp="14w6wxec15fo5">
          <button type="button" onClick={startPreview} className={BUTTON_SOLID} data-id="element-745" data-mp="3j4r9p771fnx8">
            Preview as Client
          </button>
          <button type="button" onClick={() => setEditing(current => !current)} className={BUTTON_GHOST} data-id="element-746" data-mp="3e928kzfddyyt">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          <button type="button" onClick={() => setSavingTemplate(current => !current)} className={BUTTON_GHOST} data-id="element-747" data-mp="30fb78ebslle6">
            Save as Template
          </button>
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_GHOST} data-id="element-748" data-mp="398o76089xn9r">
              Reopen Project
            </button> : <button type="button" onClick={() => setConfirmArchive(true)} className={BUTTON_GHOST} data-id="element-749" data-mp="2cnw1ebkt4ly6">
              Archive Project
            </button>}
        </div>

        <ClientLink slug={project.slug} projectId={project.id} data-id="element-750" data-mp="2zdnh0rok9o4f" />

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-751" data-mp="344dms60wfszn">
            <p className="font-display text-2xl leading-snug" data-id="element-752" data-mp="393gtrw0fw9a9">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-753" data-mp="3d4scriot5w9h">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-754" data-mp="3n2pz20n3xizg">
              <button type="button" onClick={() => {
            archiveProject();
            setConfirmArchive(false);
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-755" data-mp="3n0vong9gp99m">
                Archive Project
              </button>
              <button type="button" onClick={() => setConfirmArchive(false)} className={BUTTON_QUIET} data-id="element-756" data-mp="1gdegi65g1gmx">
                Cancel
              </button>
            </div>
          </div>}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-757" data-mp="243s3hi968pan">
            <label className="block" data-id="element-758" data-mp="1z4xkaf7akyq0">
              <span className="label block text-paper/40" data-id="element-759" data-mp="1z5yw4lw4tp8s">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-760" data-mp="1u4t28kqqjvxo" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-761" data-mp="dqt5tjr3mof6">
              <button type="submit" className={BUTTON_SOLID} data-id="element-762" data-mp="31xtdlqo98q31">
                Save Template
              </button>
              <button type="button" onClick={() => setSavingTemplate(false)} className={BUTTON_QUIET} data-id="element-763" data-mp="35z4th4hbtwy1">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setEditing(false)} data-id="element-764" data-mp="2x0t4t800t988" />}
      </header>

      <section aria-labelledby="deliverables-admin" className="mt-14" data-id="element-765" data-mp="2i896qlrq9rxn">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-766" data-mp="2n0shzc2y0aza">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-767" data-mp="29a6wrgq0noy8">
            Deliverables
          </h2>
          <p className="label-sm m-0 text-paper/35" data-id="element-768" data-mp="2e8qltpzwpgut">
            {reviewCount} in review · {finalCount} final delivery
            {hidden > 0 ? ` · ${hidden} hidden from client` : ''}
          </p>
        </div>

        {project.deliverables.length === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-769" data-mp="1knpq1im71wxs">
            No deliverables yet. Add whatever this project requires.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-770" data-mp="1um5knvkfjoc1">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-771" data-mp="28f4y7s0nj8gy" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-772" data-mp="2n8q9opdfnocf" />
      </section>

      <ClientAccess data-id="element-773" data-mp="2icsvoppn6u2t" />
      <PortalTeam data-id="element-774" data-mp="2idb1acy72kne" />
    </div>;
}